import { DynamicForm } from "@shared/components/DynamicForm";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Separator } from "@shared/ui/separator";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Edit, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useCreateSystemTypeParameter,
  useDeleteSystemTypeParameter,
  useGetSystemTypeParameter,
  useUpdateSystemTypeParameter,
} from "../../hooks";

export const ParameterDialog = ({
  showParameters,
  setShowParameters,
  selectedSystemTypeForParams,
}) => {
  console.log(selectedSystemTypeForParams);
  const remoteUtilityId = getRemoteUtilityId();
  const [editingParameter, setEditingParameter] = useState(null);

  const createSystemTypeParameter = useCreateSystemTypeParameter(
    selectedSystemTypeForParams?.code
  );
  const updateSystemTypeParameter = useUpdateSystemTypeParameter(
    selectedSystemTypeForParams?.code
  );
  const { data: systemTypeParameterData } = useGetSystemTypeParameter(
    "system_parameter",
    true,
    selectedSystemTypeForParams?.code
  );
  const deleteSystemTypeParameter = useDeleteSystemTypeParameter(
    selectedSystemTypeForParams?.code
  );

  const isEditMode = Boolean(editingParameter);

  // Form field definitions
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Parameter Name",
      placeholder: "Enter parameter name...",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "unit",
      type: "text",
      label: "Unit of Measurement",
      placeholder: "Enter unit...",
      required: false,
      className: "col-span-1 mt-3",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter parameter description...",
      rows: 2,
      required: false,
      className: "col-span-1 mt-3",
    },
  ];

  // Create form with proper default values
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      unit: "",
      description: "",
    },
  });

  // Populate form when edit data changes or dialog opens
  useEffect(() => {
    if (showParameters) {
      const initialValues = {
        name: editingParameter?.name || "",
        unit: editingParameter?.unit || "",
        description: editingParameter?.description || "",
      };

      FormService.populateForm(form, initialValues, formFields);
    }
  }, [showParameters, editingParameter]);

  const createPayload = (basePayload: any, formValues: any) => {
    const payload = { ...basePayload };

    if (formValues.unit) {
      payload.unit = formValues.unit;
    }
    if (formValues.description) {
      payload.description = formValues.description;
    }

    return payload;
  };

  const handleSubmit = async (formValues: any) => {
    try {
      const operation = isEditMode ? "update" : "create";

      if (operation === "update") {
        const basePayload = {
          code: editingParameter.code,
          remote_utility_id: remoteUtilityId,
          name: formValues.name,
        };
        const payload = createPayload(basePayload, formValues);
        await updateSystemTypeParameter.mutateAsync(payload);
        toast({
          title: "Parameter Updated",
          description: "Parameter has been updated successfully.",
        });
      } else {
        const basePayload = {
          code: selectedSystemTypeForParams?.code + "_" + formValues.name,
          remote_utility_id: remoteUtilityId,
          name: formValues.name,
        };
        const payload = createPayload(basePayload, formValues);
        await createSystemTypeParameter.mutateAsync(payload);
        toast({
          title: "Parameter Created",
          description: "Parameter has been created successfully.",
        });
      }

      handleReset();
    } catch (error) {
      toast({
        title: `Failed to create Parameter: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.reset({
      name: "",
      unit: "",
      description: "",
    });
    setEditingParameter(null);
  };

  // Handle dialog close
  const handleClose = () => {
    handleReset();
    setShowParameters(false);
  };

  const handleDeleteParameter = async (parameter: any) => {
    try {
      await deleteSystemTypeParameter.mutateAsync({
        remote_utility_id: remoteUtilityId,
        code: parameter.code,
      });
      toast({
        title: "Parameter Deleted",
        description: "Parameter has been deleted successfully.",
      });
    } catch (error) {
      console.error("Delete parameter error:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the parameter.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={showParameters} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Manage Parameters for: {selectedSystemTypeForParams?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add/Edit Parameter Form */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium mb-4">
              {isEditMode ? "Edit Parameter" : "Add New Parameter"}
            </h4>

            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                submitButtonText: isEditMode
                  ? "Update Parameter"
                  : "Add Parameter",
                resetButtonText: "Clear",
                spacing: "comfortable",
                classes: {
                  form: "space-y-4",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer: "flex justify-end gap-3 pt-4 mt-4",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-sm text-gray-500 mt-1",
                  container: "space-y-1",
                },
              }}
            />
          </div>

          <Separator />

          {/* Existing Parameters */}
          <div className="space-y-4">
            <h4 className="font-medium">Existing Parameters</h4>
            {selectedSystemTypeForParams?.parameters?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No parameters defined yet.
              </p>
            ) : (
              <div className="space-y-2">
                {systemTypeParameterData?.map((parameter, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="font-medium">{parameter?.name}</h5>
                        {parameter?.unit && (
                          <Badge variant="outline">{parameter?.unit}</Badge>
                        )}
                      </div>
                      {parameter?.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {parameter?.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingParameter(parameter)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteParameter(parameter)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
